import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { HeatmapChart } from "@carbon/charts-react";
import { Modal } from "@carbon/react";
import ChartPanelWrapper from "../ChartPanelWrapper";
import { selectFilterConfig } from "../../../../features/dashboard/dashboardSlice";
import { dashboardDataRequestThunk } from "../../../../features/dashboard/dashboardThunks";
import { selectCameraFilter, selectDashboardHasCameraFilter, selectHeatmapOptions } from "../../../../features/dashboard/heatmapSelectors";
import { transformHeatmapData } from "./heatmapUtils";
import HeatmapCameraSelector from "./cameraSelector";
import "./_index.scss"

const Index = ({
  chart,
  overlayColor = "rgba(150, 151, 255, 0.3)",
  backgroundImageUrl = null,
}) => {
  const dispatch = useDispatch();
  const [thumbnailModalOpen, setThumbnailModalOpen] = useState(false);
  const [selectedCamera, setSelectedCamera] = useState('');

  const filterConfig = useSelector(selectFilterConfig);
  const cameraFilter = useSelector(selectCameraFilter);
  const dashboardHasCameraFilter = useSelector(selectDashboardHasCameraFilter);
  const chartOptions = useSelector((state) => selectHeatmapOptions(state, chart));

  const camSelectorOptions = useMemo(() => cameraFilter?.options || [], [cameraFilter]);
  const camSelectorDisabled = camSelectorOptions.length === 0;

  useEffect(() => {
    if (dashboardHasCameraFilter) {
      setSelectedCamera('');
    }
  }, [dashboardHasCameraFilter]);

  useEffect(() => {
    if (!dashboardHasCameraFilter && camSelectorOptions.length > 0 && !selectedCamera) {
      setSelectedCamera(camSelectorOptions[0]?.value || '');
    }
  }, [camSelectorOptions, selectedCamera, dashboardHasCameraFilter]);

  const effectiveCamera = useMemo(() => {
    if (dashboardHasCameraFilter) return null;
    if (!cameraFilter || !selectedCamera) return null;
    const nullLabel = cameraFilter.allow_null ? cameraFilter.null_label : undefined;
    if (selectedCamera === nullLabel || selectedCamera === 'all') return null;
    return selectedCamera;
  }, [cameraFilter, selectedCamera, dashboardHasCameraFilter]);

  const onCameraChange = (val) => {
    if (val === selectedCamera) return;

    setSelectedCamera(val);
    if (cameraFilter) {
      dispatch(
        dashboardDataRequestThunk({
          dashboardId: chart.dashboard,
          panel: chart,
          filterConfig,
          filterValues: { [cameraFilter.id]: val }
        })
      ).catch((err) => {
        console.error(`Failed to fetch heatmap data with camera filter:`, err);
      });
    }
  };

  const cameraImageUrl = backgroundImageUrl || null;
  const heatmapStyle = cameraImageUrl ? {
    backgroundImage: `linear-gradient(0deg, ${overlayColor}, ${overlayColor}), url('${cameraImageUrl}')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  } : {};

  return (
    <ChartPanelWrapper chart={chart}>
      {(panelData) => {
        const transformedData = transformHeatmapData(panelData, effectiveCamera, cameraFilter, chart);

        return (
          <div>
            <div className="heatmap-camera-bar">
              <span className="heatmap-camera-bar__title cds--cc--title">
                {chartOptions.title}
              </span>

              {!dashboardHasCameraFilter && (
                <HeatmapCameraSelector
                  options={camSelectorOptions}
                  value={selectedCamera}
                  disabled={camSelectorDisabled}
                  onChange={onCameraChange}
                />
              )}
            </div>

            <div className="heatmap-chart-wrapper" style={heatmapStyle}>
              <HeatmapChart options={chartOptions} data={transformedData} />
            </div>

            {cameraImageUrl && (
              <div className="heatmap-thumbnail-container">
                <div className="heatmap-thumbnail" onClick={() => setThumbnailModalOpen(true)}>
                  <img src={cameraImageUrl} alt="Vista de cámara" />
                  <span className="heatmap-thumbnail-label">Ver imagen completa</span>
                </div>
              </div>
            )}

            <Modal
              open={thumbnailModalOpen}
              onRequestClose={() => setThumbnailModalOpen(false)}
              modalHeading="Vista de cámara"
              passiveModal
              size="lg"
            >
              <div className="heatmap-modal-image">
                <img src={cameraImageUrl} alt="Vista de cámara completa" />
              </div>
            </Modal>
          </div>
        );
      }}
    </ChartPanelWrapper>
  );
};

export default React.memo(Index);